import clsx from "clsx";
import { FC, Suspense, useEffect, useMemo, useState } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";
import { Link } from "react-router-dom";
import { graphql, readInlineData } from "relay-runtime";
import {
  Button,
  Icons,
  SortDir,
  SortableTableHeader,
  TableFrame,
  TablePagination,
  TableTooltipLabel,
  Toggle,
  TranslationProvider,
  compareBigInts,
  compareNullableNumbers,
  compareStrings,
  useTablePagination,
  useTableSort,
  useGlobalModalContext,
  useTranslation,
} from "rujira.ui";
import { LtvGraph } from "../borrow/components/Health";
import { getCollateralTableColumns } from "../common/collateralTableColumns";
import { assetToTradeUrlSegment } from "../services/assetUrl";
import { PostitionRowDebt } from "../borrow/components/PositionAssets";
import { useIsMobile } from "../hooks/useBreakpoint";
import { SortBar } from "../common/components/SortBar";
import { StickyBar } from "../common/components/StickyBar";
import { LiquidationCard } from "./components/mobile/LiquidationCard";
import {
  LiquidationCards,
  LiquidationCardsFallback,
} from "./components/mobile/LiquidationCards";
import {
  MultiAssetLogos,
  MultiAssetValue,
} from "../borrow/components/MultiAsset";
import { CreditAccountsQuery } from "./__generated__/CreditAccountsQuery.graphql";
import {
  CreditAccountsRowFragment$data,
  CreditAccountsRowFragment$key,
} from "./__generated__/CreditAccountsRowFragment.graphql";
import { Side } from "../trade/types";
import {
  LiquidationBidModal,
  LiquidationBidModalFallback,
} from "./LiquidationBidModal";
import { LIQUIDATION_BID_DISCOUNT_DEFAULT_BPS } from "./constants";
import { liquidationBidDeviationBps } from "./utils";

type AssetSegment = {
  readonly chain: string;
  readonly type: string;
  readonly metadata: { readonly symbol: string };
};
type FinPair = {
  readonly assetBase: AssetSegment;
  readonly assetQuote: AssetSegment;
};
type BidRoute = {
  bidSymbol: string;
  collateralSymbol: string;
  pairKey: string;
  side: Side;
};

const LIQUIDATION_SORT_KEYS = [
  "collateral",
  "debt",
  "direction",
  "ltv",
] as const;
type LiquidationSortKey = (typeof LIQUIDATION_SORT_KEYS)[number];

const LIQUIDATION_COLUMNS = [
  { id: "logo", width: "3rem" },
  { id: "collateral", width: "calc(23% - 3rem)", minWidth: "11rem" },
  { id: "debt", width: "17%", minWidth: "12rem" },
  { id: "direction", width: "16%", minWidth: "5rem" },
  { id: "ltv", width: "28%", minWidth: "3rem" },
  { id: "action", width: "16%", minWidth: "8rem" },
] as const;

const QUOTE_FALLBACKS = ["USDC", "USDT", "BTC"] as const;
const DEFAULT_PAIR_KEY = "BTC/USDC";

function buildBidRoute(
  collateral: AssetSegment,
  debt: AssetSegment,
  pairs: ReadonlyMap<string, FinPair>
): BidRoute {
  const collSeg = assetToTradeUrlSegment(collateral);
  const debtSeg = assetToTradeUrlSegment(debt);
  const collateralSymbol = collateral.metadata.symbol;
  const debtSymbol = debt.metadata.symbol;

  // Rule 2: direct COLLATERAL/DEBT pair means a buy tracking bid.
  if (pairs.has(`${collSeg}/${debtSeg}`))
    return {
      bidSymbol: debtSymbol,
      collateralSymbol,
      pairKey: `${collSeg}/${debtSeg}`,
      side: Side.Quote,
    };

  // Rule 3: inverse DEBT/COLLATERAL pair means a sell tracking bid.
  if (pairs.has(`${debtSeg}/${collSeg}`))
    return {
      bidSymbol: debtSymbol,
      collateralSymbol,
      pairKey: `${debtSeg}/${collSeg}`,
      side: Side.Base,
    };

  // Rule 4: fall back through COLLATERAL/USDC, COLLATERAL/USDT, COLLATERAL/BTC.
  for (const quote of QUOTE_FALLBACKS) {
    if (pairs.has(`${collSeg}/${quote}`))
      return {
        bidSymbol: quote,
        collateralSymbol,
        pairKey: `${collSeg}/${quote}`,
        side: Side.Quote,
      };
  }

  return {
    bidSymbol: "USDC",
    collateralSymbol: "BTC",
    pairKey: DEFAULT_PAIR_KEY,
    side: Side.Quote,
  };
}

export type LiquidationDirection = {
  side: Side;
  baseSymbol: string;
  quoteSymbol: string;
};

function getLiquidationDirection(
  collateral: AssetSegment,
  debt: AssetSegment,
  pair: FinPair | undefined
): LiquidationDirection | null {
  if (!pair) return null;

  const baseSegment = assetToTradeUrlSegment(pair.assetBase);
  const baseSymbol = pair.assetBase.metadata.symbol;
  const quoteSymbol = pair.assetQuote.metadata.symbol;

  if (assetToTradeUrlSegment(collateral) === baseSegment) {
    return { side: Side.Quote, baseSymbol, quoteSymbol };
  }

  if (assetToTradeUrlSegment(debt) === baseSegment) {
    return { side: Side.Base, baseSymbol, quoteSymbol };
  }

  return null;
}

function buildBidDetailsUrl(route: BidRoute): string {
  const params = new URLSearchParams({
    type: "manual",
    order: "tracking",
  });

  if (route.side === Side.Base) params.set("side", Side.Base);
  params.set(
    "deviation",
    liquidationBidDeviationBps(
      route.side,
      LIQUIDATION_BID_DISCOUNT_DEFAULT_BPS
    ).toString()
  );

  return `/trade/${route.pairKey}?${params.toString()}`;
}

const query = graphql`
  query CreditAccountsQuery {
    ghostCredit {
      accounts(first: 20, sortBy: LTV, sortDir: DESC) {
        edges {
          node {
            account {
              address
            }
            ...CreditAccountsRowFragment
          }
        }
      }
    }
    finV2(first: 100, sortBy: VOLUME_USD, sortDir: DESC) {
      edges {
        node {
          assetBase {
            chain
            type
            metadata {
              symbol
            }
          }
          assetQuote {
            chain
            type
            metadata {
              symbol
            }
          }
        }
      }
    }
  }
`;

const LiquidationTableHeaders: FC<{
  sortBy: LiquidationSortKey;
  sortDir: SortDir;
  toggleSort: (value: LiquidationSortKey) => void;
}> = ({ sortBy, sortDir, toggleSort }) => {
  const { t } = useTranslation("liquidate");

  return (
    <>
      <th className="text-left" aria-hidden="true" />
      <SortableTableHeader
        label={
          <TableTooltipLabel
            label={t("liquidationCollateral")}
            tooltip={`<div class="w-30 text-left">${t("liquidationCollateralHeaderTooltip")}</div>`}
          />
        }
        value="collateral"
        active={sortBy}
        dir={sortDir}
        onSort={toggleSort}
      />
      <SortableTableHeader
        label={
          <TableTooltipLabel
            label={t("liquidationDebt")}
            tooltip={`<div class="w-30 text-left">${t("liquidationDebtHeaderTooltip")}</div>`}
          />
        }
        value="debt"
        active={sortBy}
        dir={sortDir}
        onSort={toggleSort}
      />
      <SortableTableHeader
        label={
          <TableTooltipLabel
            ariaLabel={t("liquidationDirectionHeaderAria")}
            label={t("liquidationDirection")}
            tooltip={`<div class="w-30 text-left">${t("liquidationDirectionHeaderTooltip")}</div>`}
          />
        }
        value="direction"
        active={sortBy}
        dir={sortDir}
        onSort={toggleSort}
        align="center"
      />
      <SortableTableHeader
        label={
          <TableTooltipLabel
            label={t("liquidationLtv")}
            tooltip={`<div class="w-30 text-left">${t("liquidationLtvHeaderTooltip")}</div>`}
          />
        }
        value="ltv"
        active={sortBy}
        dir={sortDir}
        onSort={toggleSort}
        align="center"
      />
      <th className="text-right" scope="col">
        {t("liquidationBidOnLiquidation")}
      </th>
    </>
  );
};

export const CreditAccountsFallback: FC = () => {
  const isMobile = useIsMobile();
  const { sortBy, sortDir, toggleSort } = useLiquidationSort();

  if (isMobile) return <LiquidationCardsFallback />;

  return (
    <TableFrame
      className="liquidation-table collateral-table"
      columns={getCollateralTableColumns(LIQUIDATION_COLUMNS, [])}
      stickyFirstColumn
      tableClassName="table table--fixed table--big condensed mb-0">
      <thead>
        <tr>
          <LiquidationTableHeaders
            sortBy={sortBy}
            sortDir={sortDir}
            toggleSort={toggleSort}
          />
        </tr>
      </thead>
      <tbody>
        {[...Array(4)].map((_, i) => (
          <tr key={`skeleton_${i}`}>
            <td className="text-left">
              <div className="skeleton h-3.5 w-3.5 br-round" />
            </td>
            <td className="text-left">
              <div className="skeleton h-1.5 w-8 br-2" />
            </td>
            <td>
              <div className="skeleton h-1.5 w-10 br-2" />
            </td>
            <td>
              <div className="flex jc-c">
                <div className="skeleton h-1.5 w-4 br-2" />
              </div>
            </td>
            <td className="text-center">
              <div className="skeleton h-1.5 w-5 br-2 mx-a" />
            </td>
            <td>
              <div className="skeleton h-2 w-8 br-2 ml-a" />
            </td>
          </tr>
        ))}
      </tbody>
    </TableFrame>
  );
};

export const CreditAccounts: FC = () => {
  const [q, load] = useQueryLoader<CreditAccountsQuery>(query);
  useEffect(() => {
    load({});
  }, []);
  if (!q) return <CreditAccountsFallback />;
  return (
    <TranslationProvider namespace="portfolio">
      <Suspense fallback={<CreditAccountsFallback />}>
        <Content q={q} />
      </Suspense>
    </TranslationProvider>
  );
};

const Content: FC<{ q: PreloadedQuery<CreditAccountsQuery> }> = ({ q }) => {
  const data = usePreloadedQuery<CreditAccountsQuery>(query, q);
  const isMobile = useIsMobile();
  const [hideSmall, setHideSmall] = useState(true);
  const { t } = useTranslation();
  const { sortBy, sortDir, toggleSort } = useLiquidationSort();

  const pairs = useMemo(
    () =>
      new Map<string, FinPair>(
        data.finV2?.edges?.flatMap((edge) =>
          edge?.node
            ? [
                [
                  `${assetToTradeUrlSegment(edge.node.assetBase)}/${assetToTradeUrlSegment(edge.node.assetQuote)}`,
                  {
                    assetBase: edge.node.assetBase,
                    assetQuote: edge.node.assetQuote,
                  },
                ] as const,
              ]
            : []
        ) ?? []
      ),
    [data.finV2?.edges]
  );

  const accounts = useMemo(
    () =>
      data.ghostCredit?.accounts.edges?.flatMap((edge) =>
        edge?.node
          ? [createLiquidationRow(edge.node.account.address, edge.node, pairs)]
          : []
      ) ?? [],
    [data.ghostCredit?.accounts.edges, pairs]
  );
  const sortedAccounts = useMemo(
    () =>
      accounts
        .filter(
          (account) => !hideSmall || account.data.valueUsd >= 100_000_000n
        )
        .sort((a, b) => compareLiquidationRows(a, b, sortBy, sortDir)),
    [accounts, hideSmall, sortBy, sortDir]
  );
  const columns = useMemo(
    () =>
      getCollateralTableColumns(
        LIQUIDATION_COLUMNS,
        sortedAccounts.map((account) => account.data)
      ),
    [sortedAccounts]
  );
  const { pageRows: visibleAccounts, pagination } = useTablePagination(
    sortedAccounts,
    { resetKey: `${sortBy}:${sortDir}:${hideSmall}` }
  );
  const rows = visibleAccounts.map((account) => (
    <Row key={account.address} account={account} asCard={isMobile} />
  ));

  const sortOptions: readonly {
    key: LiquidationSortKey;
    label: string;
  }[] = [
    { key: "collateral", label: t("liquidationCollateral") },
    { key: "debt", label: t("liquidationDebt") },
    { key: "direction", label: t("liquidationDirection") },
    { key: "ltv", label: t("liquidationLtv") },
  ];

  const lowValueToggle = (
    <Toggle
      label={
        isMobile
          ? t("common:hideLowBalance")
          : t("common:hideLowValueBalances")
      }
      className="toggle--xs color-white as-c"
      checked={hideSmall}
      onChange={(e) => setHideSmall(e.currentTarget.checked)}
    />
  );

  if (isMobile)
    return (
      <>
        <div className="flex jc-e mb-2">{lowValueToggle}</div>
        <StickyBar
          collapseLabel="Show Sorter"
          className="sticky-bar--clear px-2 py-2">
          <SortBar
            options={sortOptions}
            sortKey={sortBy}
            sortDir={sortDir}
            onKeyChange={toggleSort}
            onDirToggle={() => toggleSort(sortBy)}
          />
        </StickyBar>
        <LiquidationCards>{rows}</LiquidationCards>
        <TablePagination {...pagination} />
      </>
    );

  return (
    <>
      <div
        className="text-right mb-2"
        style={{ position: "absolute", top: "0.5rem", right: "2rem" }}>
        {lowValueToggle}
      </div>
      <TableFrame
        className="liquidation-table collateral-table mt-3"
        columns={columns}
        stickyFirstColumn
        tableClassName="table table--fixed table--big condensed mb-0">
        <thead>
          <tr>
            <LiquidationTableHeaders
              sortBy={sortBy}
              sortDir={sortDir}
              toggleSort={toggleSort}
            />
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </TableFrame>
      <TablePagination {...pagination} />
    </>
  );
};

const fragment = graphql`
  fragment CreditAccountsRowFragment on GhostCreditAccount @inline {
    collaterals {
      collateral {
        __typename
        ... on Balance {
          asset {
            asset
            chain
            type
            metadata {
              symbol
            }
          }
          amount
        }
      }
      valueFull
      valueAdjusted
    }
    debts {
      value
      debt {
        current
        borrower {
          vault {
            status {
              debtRate
            }
          }
          asset {
            chain
            type
            metadata {
              symbol
            }
          }
        }
      }
    }
    ltv
    valueUsd
  }
`;

type LiquidationRow = {
  address: string;
  data: CreditAccountsRowFragment$data;
  collateralUsd: bigint;
  debtUsd: bigint;
  bidRoute: BidRoute | null;
  detailsTo: string | null;
  direction: LiquidationDirection | null;
};

const createLiquidationRow = (
  address: string,
  key: CreditAccountsRowFragment$key,
  pairs: ReadonlyMap<string, FinPair>
): LiquidationRow => {
  const data = readInlineData(fragment, key);
  const primaryCollateral = data.collaterals.reduce<
    (typeof data.collaterals)[number] | undefined
  >(
    (highest, collateral) =>
      collateral.collateral.__typename === "Balance" &&
      (!highest || collateral.valueFull > highest.valueFull)
        ? collateral
        : highest,
    undefined
  );
  const primaryDebt = data.debts.reduce<
    (typeof data.debts)[number] | undefined
  >(
    (highest, debt) =>
      !highest || debt.value > highest.value ? debt : highest,
    undefined
  );
  const bidRoute =
    primaryCollateral?.collateral.__typename === "Balance" && primaryDebt
      ? buildBidRoute(
          primaryCollateral.collateral.asset,
          primaryDebt.debt.borrower.asset,
          pairs
        )
      : null;
  const detailsTo = bidRoute ? buildBidDetailsUrl(bidRoute) : null;
  const direction =
    bidRoute &&
    primaryCollateral?.collateral.__typename === "Balance" &&
    primaryDebt
      ? getLiquidationDirection(
          primaryCollateral.collateral.asset,
          primaryDebt.debt.borrower.asset,
          pairs.get(bidRoute.pairKey)
        )
      : null;

  return {
    address,
    data,
    collateralUsd: sumBigInts(
      data.collaterals.map(({ valueFull }) => valueFull)
    ),
    debtUsd: sumBigInts(data.debts.map(({ value }) => value)),
    bidRoute,
    detailsTo,
    direction,
  };
};

const useLiquidationSort = () =>
  useTableSort<LiquidationSortKey>({
    initialBy: "ltv",
    initialDir: SortDir.Desc,
    defaultDir: defaultLiquidationSortDir,
  });

const defaultLiquidationSortDir = (value: LiquidationSortKey): SortDir =>
  value === "direction" ? SortDir.Asc : SortDir.Desc;

const compareLiquidationRows = (
  a: LiquidationRow,
  b: LiquidationRow,
  sortBy: LiquidationSortKey,
  sortDir: SortDir
): number => {
  const result = (() => {
    switch (sortBy) {
      case "collateral":
        return compareBigInts(a.collateralUsd, b.collateralUsd, sortDir);
      case "debt":
        return compareBigInts(a.debtUsd, b.debtUsd, sortDir);
      case "direction":
        return compareNullableNumbers(
          liquidationDirectionRank(a.direction),
          liquidationDirectionRank(b.direction),
          sortDir
        );
      case "ltv":
        return compareBigInts(a.data.ltv, b.data.ltv, sortDir);
      default:
        return 0;
    }
  })();

  return result || compareStrings(a.address, b.address);
};

const liquidationDirectionRank = (
  direction: LiquidationDirection | null
): number | null =>
  direction ? (direction.side === Side.Quote ? 0 : 1) : null;

const sumBigInts = (values: readonly bigint[]): bigint =>
  values.reduce((total, value) => total + value, 0n);

export const Row: FC<{
  account: LiquidationRow;
  asCard?: boolean;
}> = ({ account, asCard }) => {
  const { data, bidRoute, detailsTo, direction } = account;
  const { showModal, hideModal } = useGlobalModalContext();
  const { t } = useTranslation("liquidate");

  const openBid = () => {
    if (!bidRoute) return;
    showModal({
      backgroundClose: false,
      children: (
        <Suspense
          fallback={
            <LiquidationBidModalFallback
              bidSymbol={bidRoute.bidSymbol}
              collateralSymbol={bidRoute.collateralSymbol}
              side={bidRoute.side}
            />
          }>
          <LiquidationBidModal
            bidSymbol={bidRoute.bidSymbol}
            collateralSymbol={bidRoute.collateralSymbol}
            hideModal={hideModal}
            pairKey={bidRoute.pairKey}
            side={bidRoute.side}
          />
        </Suspense>
      ),
    });
  };

  if (asCard)
    return (
      <LiquidationCard
        data={data}
        direction={direction}
        detailsTo={detailsTo}
        onBid={openBid}
      />
    );

  return (
    <tr>
      <td className="text-left">
        <MultiAssetLogos collaterals={data.collaterals} />
      </td>
      <td className="text-left">
        <MultiAssetValue collaterals={data.collaterals} />
      </td>
      <td>
        {data.debts.map((x, idx) => (
          <PostitionRowDebt key={idx} {...x} />
        ))}
      </td>
      <td className="text-center">
        <DirectionPill direction={direction} />
      </td>
      <td className="text-center">
        <div className="iflex">
          <LtvGraph ltv={BigInt(data.ltv)} />
        </div>
      </td>
      <td>
        {bidRoute && detailsTo && (
          <div className="flex dir-c gap-0.5 w-14 ml-a">
            <Button
              className="button--xs w-full"
              label={t("liquidationBidPlaceBid")}
              onClick={openBid}
            />
            <Link
              to={detailsTo}
              className="button button--xs button--outline button--grey w-full button--icon-right">
              {t("liquidationBidDetails")}
              <Icons.ArrowUpRight />
            </Link>
          </div>
        )}
      </td>
    </tr>
  );
};

export const DirectionPill: FC<{
  direction: LiquidationDirection | null;
}> = ({ direction }) => {
  const { t } = useTranslation("liquidate");

  if (!direction) return <span className="color-grey">-</span>;

  const isBuy = direction.side === Side.Quote;
  const tooltip = isBuy
    ? t("liquidationDirectionBuyTooltip", {
        base: direction.baseSymbol,
        quote: direction.quoteSymbol,
      })
    : t("liquidationDirectionSellTooltip", {
        base: direction.baseSymbol,
        quote: direction.quoteSymbol,
      });

  return (
    <span
      className={clsx("tag tag--borderless", {
        "tag--teal": isBuy,
        "tag--red": !isBuy,
      })}
      tabIndex={0}
      aria-label={tooltip}
      data-tooltip-id="global-tip"
      data-tooltip-content={tooltip}>
      {isBuy ? t("liquidationDirectionBuy") : t("liquidationDirectionSell")}
    </span>
  );
};
